import { getRegionLocation, regionSearchText, normalizeLocationSearch } from '../../utils/regionLocation';
import React, { useState, useMemo, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Chip,
  Typography,
  InputBase,
  Paper,
  List,
  ListItem,
  ListItemText,
  Fade,
  keyframes,
  alpha,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Region } from '../../services/api';
import { ALL_PROVIDER_TYPES, isProviderUiSupported } from '../../utils/uiSupportedProviders';

export interface SmartFilterBarHandle {
  addFilter: (query: string, label?: string) => void;
  /** Returns whether a filter with the given query is currently active (for selected-state UI) */
  hasFilter: (query: string) => boolean;
}

interface SmartFilterBarProps {
  regions: Region[];
  onFilteredRegionsChange: (filtered: Region[]) => void;
  /** Called when active filters change so parent can show selected state on clickable values */
  onActiveFiltersChange?: (filterQueries: string[]) => void;
  scenarioId?: string | null;
}

interface ActiveFilter {
  id: string;
  query: string;
  label: string;
}

interface Suggestion {
  type: 'completion' | 'region';
  value: string;
  display: string;
  description?: string;
}

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** Normalize filter query for comparison (toggle / hasFilter). Export for use in RegionsPanel. */
export function normalizeFilterQueryForCompare(q: string): string {
  return q.trim().toLowerCase();
}

const SmartFilterBar = forwardRef<SmartFilterBarHandle, SmartFilterBarProps>(function SmartFilterBar(
  { regions, onFilteredRegionsChange, onActiveFiltersChange, scenarioId },
  ref
) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [query, setQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [runningOnly, setRunningOnly] = useState(false);
  const [providerTypeFilters, setProviderTypeFilters] = useState<string[]>([]);
  const [hasHydrated, setHasHydrated] = useState(false);

  const STORAGE_KEY = 'operations.regionsFiltersState';

  const addFilterFromValues = React.useCallback((filterQuery: string, label?: string) => {
    const trimmed = filterQuery.trim();
    if (!trimmed) return;
    const normalized = normalizeFilterQueryForCompare(trimmed);
    setActiveFilters((prev) => {
      const already = prev.find((f) => normalizeFilterQueryForCompare(f.query) === normalized);
      if (already) {
        return prev.filter((f) => normalizeFilterQueryForCompare(f.query) !== normalized);
      }
      const newFilter: ActiveFilter = {
        id: Date.now().toString(),
        query: trimmed,
        label: label?.trim() ?? trimmed,
      };
      return [...prev, newFilter];
    });
    setQuery('');
    setShowSuggestions(false);
  }, []);

  const hasFilter = React.useCallback((filterQuery: string) => {
    const normalized = normalizeFilterQueryForCompare(filterQuery);
    return activeFilters.some((f) => normalizeFilterQueryForCompare(f.query) === normalized);
  }, [activeFilters]);

  useImperativeHandle(ref, () => ({
    addFilter: addFilterFromValues,
    hasFilter,
  }), [addFilterFromValues, hasFilter]);

  useEffect(() => {
    onActiveFiltersChange?.(activeFilters.map((f) => f.query));
  }, [activeFilters, onActiveFiltersChange]);

  const resetFilters = React.useCallback(() => {
    setActiveFilters([]);
    setQuery('');
    setRunningOnly(false);
    setProviderTypeFilters([]);
  }, []);

  // Parse filter query (supports negation and multiple formats)
  const parseFilterQuery = (queryStr: string) => {
    const trimmed = queryStr.trim().toLowerCase();
    if (!trimmed) return null;

    const filters: {
      continent?: string;
      providerType?: string;
      providerName?: string;
      regionName?: string;
      regionCode?: string;
      location?: string;
      status?: string;
      configured?: string;
      bots?: string;
      capacity?: string;
      freeText?: string;
      negations: Set<string>;
    } = { negations: new Set() };

    // Improved regex to properly handle quoted values: key="value" or key=value
    const filterPattern = /(!?)(\w+)(?:=|:)\s*"([^"]*)"|(!?)(\w+)(?:=|:)\s*([^\s]+)/gi;
    let match;
    let lastIndex = 0;
    const freeTextParts: string[] = [];

    while ((match = filterPattern.exec(trimmed)) !== null) {
      if (match.index > lastIndex) {
        freeTextParts.push(trimmed.substring(lastIndex, match.index).trim());
      }
      lastIndex = match.index + match[0].length;

      // Extract values from either quoted or unquoted pattern
      const negation = match[1] || match[4];
      const key = (match[2] || match[5]).toLowerCase();
      let value = (match[3] !== undefined ? match[3] : match[6]).toLowerCase();
      
      // Handle wildcards: convert * to regex-friendly pattern for includes check
      // Remove * characters since we'll use includes() which already does partial matching
      value = value.replace(/\*/g, '');

    if (key === 'continent') {
        filters.continent = value;
        if (negation) filters.negations.add('continent');
      } else if (key === 'provider_type' || key === 'type') {
        filters.providerType = value;
        if (negation) filters.negations.add('providerType');
      } else if (key === 'provider' || key === 'provider_name') {
        filters.providerName = value;
        if (negation) filters.negations.add('providerName');
      } else if (key === 'region' || key === 'region_name' || key === 'name') {
        filters.regionName = value;
        if (negation) filters.negations.add('regionName');
      } else if (key === 'region_code' || key === 'code') {
        filters.regionCode = value;
        if (negation) filters.negations.add('regionCode');
      } else if (key === 'location') {
        filters.location = value;
        if (negation) filters.negations.add('location');
    } else if (key === 'status') {
      filters.status = value;
      if (negation) filters.negations.add('status');
    } else if (key === 'configured') {
      filters.configured = value;
      if (negation) filters.negations.add('configured');
    } else if (key === 'bots' || key === 'machine_count') {
      filters.bots = value;
      if (negation) filters.negations.add('bots');
    } else if (key === 'capacity') {
      filters.capacity = value;
      if (negation) filters.negations.add('capacity');
      }
    }

    if (lastIndex < trimmed.length) {
      freeTextParts.push(trimmed.substring(lastIndex).trim());
    }

    const freeText = freeTextParts.filter(Boolean).join(' ').trim();
    if (freeText) {
      filters.freeText = freeText;
    }

    return filters;
  };

  // Apply all active filters to regions
  const matchesNumericFilter = (rawFilter: string | undefined, actualValue: number): boolean => {
    if (!rawFilter) return true;
    const normalized = rawFilter.replace(/\s/g, '');
    const match = normalized.match(/^(>=|<=|>|<|=)?(-?\d+(?:\.\d+)?)$/);
    if (!match) return false;
    const operator = match[1] || '=';
    const target = Number(match[2]);
    switch (operator) {
      case '>':
        return actualValue > target;
      case '>=':
        return actualValue >= target;
      case '<':
        return actualValue < target;
      case '<=':
        return actualValue <= target;
      default:
        return actualValue === target;
    }
  };

  const filteredRegions = useMemo(() => {
    const liveQuery = query.trim();
    const activeQueries = liveQuery
      ? [...activeFilters.map(filter => filter.query), liveQuery]
      : activeFilters.map(filter => filter.query);

    const parsedQueries = activeQueries
      .map((activeQuery) => ({
        query: activeQuery,
        filters: parseFilterQuery(activeQuery)
      }))
      .filter((entry) => entry.filters);

    const matchesStringFilter = (value: string, rawFilter: string, isNegated: boolean) => {
      value = normalizeLocationSearch(value);
      rawFilter = normalizeLocationSearch(rawFilter);
      const terms = rawFilter
        .split(/[,&|]/)
        .map(term => term.trim())
        .filter(Boolean);
      if (terms.length === 0) return true;
      const matchesAny = terms.some(term => value.includes(term));
      return isNegated ? !matchesAny : matchesAny;
    };

    const matchesParsedFilters = (region: Region, filters: ReturnType<typeof parseFilterQuery>) => {
      if (!filters) return true;

      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('Parsed filters:', filters);
      }

      const provider = region.provider;

      if (filters.continent) {
        const isNegated = filters.negations.has('continent');
        const value = getRegionLocation(region).continent.toLowerCase();
        if (!matchesStringFilter(value, filters.continent, isNegated)) return false;
      }
      if (filters.providerType) {
        if (!provider) return false;
        const isNegated = filters.negations.has('providerType');
        const value = (provider.provider_type || '').toLowerCase();
        if (!matchesStringFilter(value, filters.providerType, isNegated)) return false;
      }
      if (filters.providerName) {
        if (!provider) return false;
        const isNegated = filters.negations.has('providerName');
        const value = (provider.name || '').toLowerCase();
        if (!matchesStringFilter(value, filters.providerName, isNegated)) return false;
      }
      if (filters.regionName) {
        const isNegated = filters.negations.has('regionName');
        const value = regionSearchText(region);
        if (!matchesStringFilter(value, filters.regionName, isNegated)) return false;
      }
      if (filters.regionCode) {
        const isNegated = filters.negations.has('regionCode');
        const value = (region.region_code || '').toLowerCase();
        if (!matchesStringFilter(value, filters.regionCode, isNegated)) return false;
      }
      if (filters.location) {
        const isNegated = filters.negations.has('location');
        const value = normalizeLocationSearch(getRegionLocation(region).label);
        if (!matchesStringFilter(value, filters.location, isNegated)) return false;
      }
      if (filters.status) {
        const isNegated = filters.negations.has('status');
        const value = (region.status || '').toLowerCase();
        if (!matchesStringFilter(value, filters.status, isNegated)) return false;
      }
      if (filters.configured) {
        const configuredValue = ['true', '1', 'yes', 'y'].includes(filters.configured);
        const matches = region.configured === configuredValue;
        const isNegated = filters.negations.has('configured');
        if (isNegated ? matches : !matches) return false;
      }
      if (filters.bots) {
        const matches = matchesNumericFilter(filters.bots, region.machine_count || 0);
        const isNegated = filters.negations.has('bots');
        if (isNegated ? matches : !matches) return false;
      }
      if (filters.capacity) {
        const capacityValue = (region.capacity ?? (region as any).capacity ?? 0) as number;
        const matches = matchesNumericFilter(filters.capacity, capacityValue);
        const isNegated = filters.negations.has('capacity');
        if (isNegated ? matches : !matches) return false;
      }

      if (filters.freeText) {
        const searchText = `${regionSearchText(region)} ${provider?.name || ''} ${provider?.provider_type || ''}`.toLowerCase();
        return matchesStringFilter(searchText, filters.freeText, false);
      }

      return true;
    };

    return regions.filter(region => {
      if (runningOnly && (region.machine_count || 0) <= 0) return false;
      if (providerTypeFilters.length > 0) {
        const providerType = region.provider?.provider_type?.toLowerCase();
        if (!providerType || !providerTypeFilters.includes(providerType)) return false;
      }
      if (parsedQueries.length === 0) return true;
      return parsedQueries.some(({ filters }) => matchesParsedFilters(region, filters));
    });
  }, [
    regions,
    activeFilters,
    query,
    runningOnly,
    providerTypeFilters
  ]);

  useEffect(() => {
    onFilteredRegionsChange(filteredRegions);
  }, [filteredRegions, onFilteredRegionsChange]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        resetFilters();
        setHasHydrated(true);
        return;
      }
      const parsed = JSON.parse(stored) as {
        scenarioId?: string | null;
        activeFilters?: ActiveFilter[];
        runningOnly?: boolean;
        providerTypeFilters?: string[];
      };
      setActiveFilters(Array.isArray(parsed.activeFilters) ? parsed.activeFilters : []);
      setRunningOnly(typeof parsed.runningOnly === 'boolean' ? parsed.runningOnly : false);
      setProviderTypeFilters(Array.isArray(parsed.providerTypeFilters) ? parsed.providerTypeFilters : []);
      setQuery('');
    } catch {
      resetFilters();
    } finally {
      setHasHydrated(true);
    }
  }, [scenarioId, resetFilters]);

  useEffect(() => {
    if (!hasHydrated) return;
    const payload = {
      scenarioId: scenarioId ?? null,
      activeFilters,
      runningOnly,
      providerTypeFilters,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Ignore storage quota / privacy-mode write failures.
    }
  }, [
    hasHydrated,
    scenarioId,
    activeFilters,
    runningOnly,
    providerTypeFilters,
  ]);

  // Generate smart suggestions
  const suggestions = useMemo<Suggestion[]>(() => {
    if (!query.trim()) return [];

    const sug: Suggestion[] = [];
    const lowerQuery = query.toLowerCase();
    const lastWord = query.split(/\s+/).pop()?.toLowerCase() || '';

    // Get unique values from regions for suggestions
    const continents = Array.from(new Set(regions.map(r => getRegionLocation(r).continent).filter((c): c is string => Boolean(c))));
    const providerTypes = Array.from(new Set(regions.map(r => r.provider?.provider_type).filter(Boolean)));
    const providerNames = Array.from(new Set(regions.map(r => r.provider?.name).filter((n): n is string => Boolean(n))));

    const filterTemplates = [
      { key: 'continent', values: continents.map(c => c.toLowerCase()) },
      { key: 'provider_type', values: providerTypes.map(t => t ? t.toLowerCase() : '').filter(Boolean) },
      { key: 'provider_name', values: providerNames.map(n => n.toLowerCase()) },
      { key: 'status', values: ['available', 'cooldown', 'unavailable'] },
    ];

    // Suggest filter keys when no operator present
    if (!lowerQuery.includes('=') && !lowerQuery.includes(':')) {
      const filterKeys = [
        'continent',
        'provider_type',
        'provider_name',
        'region_name',
        'region_code',
        'location',
        'status',
        'configured',
        'bots',
        'capacity',
        '!continent',
        '!provider_type'
      ];
      filterKeys.forEach(key => {
        if (key.toLowerCase().includes(lastWord) && lastWord.length > 0) {
          sug.push({
            type: 'completion',
            value: query.replace(new RegExp(lastWord + '$', 'i'), key + '="'),
            display: key,
            description: `Filter by ${key.replace('!', 'not ')}`,
          });
        }
      });
    }

    // Suggest filter values after operator
    filterTemplates.forEach(({ key, values }) => {
      const pattern = new RegExp(key + '[:=]', 'i');
      if (pattern.test(lowerQuery)) {
        const afterEquals = lowerQuery.split(new RegExp(key + '[:=]', 'i'))[1]?.split(/\s/)[0].replace(/"/g, '') || '';
        values.forEach(value => {
          if (value && value.includes(afterEquals)) {
            const replacement = query.replace(new RegExp(key + '[:=][^\\s]*', 'i'), `${key}="${value}"`);
            sug.push({
              type: 'completion',
              value: replacement + ' ',
              display: `${key}="${value}"`,
              description: `Set ${key} to ${value}`,
            });
          }
        });
      }
    });

    if (!/[=:]/.test(query)) {
      regions.filter(region => regionSearchText(region).includes(normalizeLocationSearch(query)))
        .slice(0, 5).forEach(region => sug.unshift({ type: 'region',
          value: `region_code="${region.region_code}"`, display: getRegionLocation(region).label,
          description: [region.region_code, region.provider?.name].filter(Boolean).join(' · ') }));
    }
    return sug.slice(0, 8);
  }, [query, regions]);

  // Handle adding a filter from the input (Enter key)
  const addFilter = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    addFilterFromValues(trimmed, trimmed);
  };

  // Handle removing a filter
  const removeFilter = (filterId: string) => {
    setActiveFilters(prev => prev.filter(f => f.id !== filterId));
  };

  const removeLastFilter = () => {
    setActiveFilters(prev => prev.slice(0, -1));
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: Suggestion) => {
    if (suggestion.type === 'completion') {
      setQuery(suggestion.value);
      inputRef.current?.focus();
    } else {
      addFilterFromValues(suggestion.value, suggestion.display);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Backspace' || e.key === 'Delete') && query.trim() === '' && activeFilters.length > 0) {
      e.preventDefault();
      removeLastFilter();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      addFilter();
      return;
    }

    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'Tab' && suggestions[selectedIndex]?.type === 'completion') {
      e.preventDefault();
      handleSuggestionClick(suggestions[selectedIndex]);
    }
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [suggestions.length]);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Filter bar: chips wrap, input always has room at the end */}
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            minHeight: 48,
            px: 1.5,
            py: 1,
            fontFamily: '"Space Mono", monospace',
            fontSize: '0.95rem',
            backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.05 : 0.08),
            border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.25)}`,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.12),
            },
            '&:focus-within': {
              boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, isDark ? 0.15 : 0.2)}`,
            },
          }}
        >
          <SearchIcon sx={{ color: theme.palette.primary.main, flexShrink: 0 }} />
          {activeFilters.map((filter) => (
            <Chip
              key={filter.id}
              label={filter.label}
              onDelete={() => removeFilter(filter.id)}
              deleteIcon={
                <CloseIcon
                  sx={{
                    color: `${theme.palette.success.main} !important`,
                    fontSize: '0.9rem',
                    '&:hover': {
                      color: `${theme.palette.error.main} !important`,
                    },
                  }}
                />
              }
              size="small"
              sx={{
                height: 22,
                backgroundColor: alpha(theme.palette.success.main, isDark ? 0.15 : 0.2),
                color: theme.palette.success.main,
                border: `1px solid ${alpha(theme.palette.success.main, isDark ? 0.4 : 0.35)}`,
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.7rem',
                fontWeight: 500,
              }}
            />
          ))}
          <InputBase
            inputRef={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            onKeyDown={handleKeyDown}
            placeholder="Type filter and press Enter"
            sx={{
              flex: '1 1 140px',
              fontFamily: '"Space Mono", monospace',
              fontSize: '0.95rem',
              color: 'text.primary',
              '& .MuiInputBase-input': {
                py: 0.75,
                minWidth: 120,
                '&::placeholder': { opacity: 0.7 },
              },
            }}
          />
        </Box>

        {/* Suggestions Dropdown */}
        <Fade in={showSuggestions && suggestions.length > 0}>
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 1,
              zIndex: 1000,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, isDark ? 0.98 : 0.96)} 0%, ${alpha(
                theme.palette.background.default,
                isDark ? 0.98 : 0.96
              )} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.25)}`,
              borderRadius: 2,
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.15)}`,
              animation: `${fadeSlideIn} 0.15s ease-out`,
              overflow: 'hidden',
            }}
          >
            <List disablePadding>
              {suggestions.map((suggestion, index) => (
                <ListItem
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{
                    cursor: 'pointer',
                    borderBottom: index < suggestions.length - 1 ? `1px solid ${alpha(theme.palette.primary.main, 0.12)}` : 'none',
                    backgroundColor: index === selectedIndex ? alpha(theme.palette.primary.main, isDark ? 0.15 : 0.12) : 'transparent',
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.1 : 0.12),
                    },
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontFamily: '"Space Mono", monospace',
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.9rem',
                        }}
                      >
                        {suggestion.display}
                      </Typography>
                    }
                    secondary={
                      suggestion.description && (
                        <Typography
                          sx={{
                            fontFamily: '"Space Mono", monospace',
                            color: theme.palette.primary.main,
                            fontSize: '0.75rem',
                            mt: 0.5,
                          }}
                        >
                          {suggestion.description}
                        </Typography>
                      )
                    }
                  />
                  <Box sx={{ ml: 2 }}>
                    <Chip
                      label="Tab"
                      size="small"
                      sx={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        color: theme.palette.primary.main,
                        fontFamily: '"Space Mono", monospace',
                        fontSize: '0.65rem',
                        height: 22,
                      }}
                    />
                  </Box>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Fade>
      </Box>

      {/* Quick Filters */}
      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontFamily: '"Space Mono", monospace',
            color: 'text.secondary',
            alignSelf: 'center',
            mr: 1,
          }}
        >
          Quick Filters
        </Typography>
        <Chip
          label="Running"
          size="small"
          onClick={() => setRunningOnly((prev) => !prev)}
          variant={runningOnly ? 'filled' : 'outlined'}
          sx={{
            fontFamily: '"Space Mono", monospace',
            fontWeight: 600,
            backgroundColor: runningOnly ? alpha(theme.palette.success.main, 0.2) : 'transparent',
            color: runningOnly ? theme.palette.success.main : 'text.secondary',
            border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
          }}
        />
        {ALL_PROVIDER_TYPES.filter(isProviderUiSupported).map((providerType) => {
          const isSelected = providerTypeFilters.includes(providerType);
          return (
            <Chip
              key={providerType}
              label={providerType.toUpperCase()}
              size="small"
              onClick={() => {
                setProviderTypeFilters((prev) =>
                  prev.includes(providerType)
                    ? prev.filter((value) => value !== providerType)
                    : [...prev, providerType]
                );
              }}
              variant={isSelected ? 'filled' : 'outlined'}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontWeight: 600,
                backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                color: isSelected ? theme.palette.primary.main : 'text.secondary',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            />
          );
        })}
      </Box>

      <Typography
        variant="caption"
        sx={{
          fontFamily: '"Space Mono", monospace',
          color: 'text.secondary',
          display: 'block',
          mt: 1.5,
        }}
      >
        Press <strong>Enter</strong> to pin a filter • Press <strong>Delete</strong> to remove the last filter
      </Typography>
    </Box>
  );
});

SmartFilterBar.displayName = 'SmartFilterBar';

export default SmartFilterBar;

